import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from 'three';
import { RenderLayers } from "client/globals";
import WorkspaceManager from "client/classes/WorkspaceManager";
import SceneCameraController from "client/classes/SceneCameraController";
import Input from "client/classes/Input";
import Engine from "client/classes/Engine";
import VisagePreview from "client/components/visageUI/VisagePreview";
import { useTypedDispatch, useTypedSelector } from "src/state/hooks";
import useEventListener from "client/components/reactUtils/useEventListener";
import { ChatCommentIcon, ExitIcon } from "client/components/Iconset";
import ModalManager from "client/components/layout/ModalManager";
import OverlayMenuItem from "./OverlayMenuItem";
import ExitScenarioDialog from "../dialogs/ExitScenarioDialog";
import { StoreActions } from "client/state/store";

const orthographicSize = 1;

const Viewport = () =>
{
	const dispatch = useTypedDispatch();

	const currentStepId = useTypedSelector(state => state.scenarioModule.currentStepId);

	const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

	const canvasRef = useRef<HTMLCanvasElement | null>();
	const canvasWrapperRef = useRef<HTMLDivElement | null>(null);

	const windowId = "viewport";

	useEventListener("resize", (e) =>
	{
		if (!canvasRef.current) return;
		console.log("resize", e, canvasRef.current.clientWidth, canvasRef.current.clientHeight);

		const newAspectRatio = canvasRef.current.clientWidth / canvasRef.current.clientHeight;

		const sceneViewData = WorkspaceManager.getSceneViewData(windowId);
		if(sceneViewData)
		{
			sceneViewData.perspectiveCamera.aspect = newAspectRatio;
			sceneViewData.activeCamera.updateProjectionMatrix();
		}

		console.log("sceneViewData", sceneViewData);
		
		setCanvasSize({ width: canvasRef.current.clientWidth, height: canvasRef.current.clientHeight });
		Input.updateSceneBounds(canvasRef.current.getBoundingClientRect());
		WorkspaceManager.setSceneViewDataProperty(windowId, 'aspectRatio', newAspectRatio);
	}, window);
	
	const canvasCallbackRef = useCallback((canvasElement: HTMLCanvasElement | null) => 
	{
		canvasRef.current = canvasElement;
		if(!canvasElement) return;

		// setup things for the engine to render into this viewport
		const sceneViewData = WorkspaceManager.getSceneViewData(windowId);
		if (!sceneViewData)
		{
			initialize();
		}
		else
		{
			WorkspaceManager.setSceneViewDataProperty(windowId, 'context', canvasElement.getContext("2d"));
		}
	}, []);

	useEffect(() =>
	{
		const sceneViewData = WorkspaceManager.getSceneViewData(windowId);
		if (!sceneViewData)
		{
			initialize();
		}

		return () =>
		{
			WorkspaceManager.disposeSceneViewData(windowId);
		}
	}, []);

	function initialize()
	{
		if (!canvasRef.current) return;
		const canvas = canvasRef.current;

		setCanvasSize({ width: canvas.clientWidth, height: canvas.clientHeight });

		const aspectRatio = canvas.clientWidth / canvas.clientHeight;
		const perspective = new THREE.PerspectiveCamera(60, aspectRatio, 0.01, 10000);
		const orthographic = new THREE.OrthographicCamera(orthographicSize * aspectRatio * -0.5, orthographicSize * aspectRatio * 0.5, orthographicSize * 0.5, orthographicSize * -0.5, 0.001, 10000);
		const cameraController = new SceneCameraController(perspective);
		cameraController.goHome(false);

		perspective.layers.enable(RenderLayers.MainCameraOnly);
		orthographic.layers.enable(RenderLayers.MainCameraOnly);

		WorkspaceManager.registerSceneView(windowId, {
			windowId: windowId,
			context: canvas.getContext("2d") as CanvasRenderingContext2D,
			perspectiveCamera: perspective,
			orthographicCamera: orthographic,
			activeCamera: perspective,
			aspectRatio: aspectRatio,
			cameraController: cameraController,
			isMainSceneView: true,
			viewCube: null,
			setCameraProjectionMode: () => {},
			renderViewCube: false,
		});
		
		Engine.getInstance().setMainSceneView(windowId);

		cameraController.update();
		cameraController.resetMouseDelta();
	}

	return (
		<div data-name={"viewport-root"}
			className={"viewportRoot fullScreen"}
		>
			<div
				data-acceptmouseinput={"true"}
				ref={canvasWrapperRef}
				data-name={"viewport-canvas-wrapper"}
			>
				<canvas
					ref={canvasCallbackRef}
					style={{
						height: "100%",
						width: "100%",
						position: "absolute",
					}}
					width={canvasSize.width.toString()}
					height={canvasSize.height.toString()}
					data-viewport={true}
				/>
			</div>			
			{ 
				currentStepId && 
					<VisagePreview wrapperRect={{ width: canvasSize.width, height: canvasSize.height }} 
						inputReceiverRef={canvasWrapperRef} 
						overlayMenu={
							<div className={"viewportMenuRoot"}
								data-name={"viewport-menu-root"}
							>
								{/* <OverlayMenuItem 
									label={"Show All Callouts"}
									data-name={"show-all-callouts"}
									icon={<ChatCommentIcon />}
									onClick={() => 
									{
										// TODO:implement the functionality itself
										dispatch(StoreActions.visage.setShowOverlayMenu(false));
									}}
								/> */}
								<OverlayMenuItem 
									label={"Exit Scenario"}
									data-name={"exit-scenario"}
									icon={<ExitIcon />}
									onClick={() => 
									{
										ModalManager.addModal({
											content: <ExitScenarioDialog />,
											allowClickOut: false,
											showBackdrop: true,
											onShutdown: () => {},
										});
									}}
								/>
							</div>
						}
					/> 
			}
		</div>
	);
}

export default Viewport;